import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchAvailability, fetchQuote, createCheckout } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ArrowRight, Check, AlertTriangle, CalendarDays } from "lucide-react";
import { SLOT_TIMES, PRODUCTS, formatGBP, endTime, formatDate, formatLongDate } from "../lib/site";

const ADAY = 86400000;

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday=0
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - day);
  return date;
}

function isoDate(d) {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export default function Book() {
  const [params, setParams] = useSearchParams();
  const initialProduct = params.get("product") || "standard";
  const initialTransmission = params.get("transmission") || "manual";

  const [product, setProduct] = useState(initialProduct in PRODUCTS && initialProduct !== "bespoke" ? initialProduct : "standard");
  const [transmission, setTransmission] = useState(["manual","auto"].includes(initialTransmission) ? initialTransmission : "manual");
  const [step, setStep] = useState(1); // 1=product, 2=slots, 3=details
  // Scroll to top whenever the booking step changes (forwards or back).
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [step]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [days, setDays] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]); // [{date, time}]
  const [quote, setQuote] = useState(null);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", pickup_address: "", pickup_town: "", pickup_postcode: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // { name?, email?, phone?, pickup_address?, pickup_town?, pickup_postcode? }

  // Refs used for mobile UX scroll-into-view affordances.
  const pickLessonsBtnRef = useRef(null);
  const slotListRef = useRef(null);
  const continueBtnRef = useRef(null);

  // If the user arrives on /book with a product preselected in the URL (e.g.
  // from the Packages page), they've already chosen — scroll the Pick Lessons
  // CTA into view on mobile so they don't have to scroll past the radio rows.
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 768) return;
    const urlProduct = params.get("product");
    if (!urlProduct || step !== 1) return;
    const id = window.setTimeout(() => {
      pickLessonsBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => window.clearTimeout(id);
    // Only on initial mount with a URL product — no need to refire on every nav.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Scroll the Pick Lessons CTA into view on small screens after the user
   *  picks a package. Desktop already shows it in the sticky sidebar. */
  const handlePickPackage = (k) => {
    setProduct(k);
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      // Delay a beat so the layout settles before we scroll.
      setTimeout(() => {
        pickLessonsBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    }
  };

  const FIELD_LABELS = {
    name: "Full name",
    email: "Email address",
    phone: "Phone number",
    pickup_address: "Address",
    pickup_town: "Town",
    pickup_postcode: "Postcode",
  };

  const updateDetail = (key, value) => {
    setDetails(d => ({ ...d, [key]: value }));
    // Clear that field's error as soon as the user starts editing.
    if (fieldErrors[key]) setFieldErrors(e => { const n = { ...e }; delete n[key]; return n; });
  };

  const validateDetails = () => {
    const errs = {};
    const trim = (v) => (v || "").trim();
    ["name", "email", "phone", "pickup_address", "pickup_town", "pickup_postcode"].forEach(k => {
      if (!trim(details[k])) errs[k] = `${FIELD_LABELS[k]} is required.`;
    });
    if (!errs.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trim(details.email))) {
      errs.email = "Please enter a valid email address.";
    }
    if (!errs.pickup_postcode) {
      // Loose UK postcode check — full + partial allowed (we'll let Stripe/Royal Mail catch edge cases).
      const pc = trim(details.pickup_postcode).toUpperCase();
      if (!/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(pc)) {
        errs.pickup_postcode = "Please enter a valid UK postcode (e.g. SO15 2BG).";
      }
    }
    return errs;
  };

  const required = PRODUCTS[product]?.slots ?? 1;
  const monthLabel = useMemo(() => {
    return weekStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  }, [weekStart]);

  useEffect(() => {
    fetchQuote(product, transmission).then(setQuote).catch(() => {});
  }, [product, transmission]);

  useEffect(() => {
    if (step !== 2) return;
    const start = isoDate(weekStart);
    const endD = new Date(weekStart);
    endD.setDate(endD.getDate() + 27); // 4 weeks
    const end = isoDate(endD);
    fetchAvailability(transmission, start, end).then(d => setDays(d.days)).catch(() => {});
  }, [step, weekStart, transmission]);

  // When we arrive on the slots screen, if the visible 4-week window has zero
  // availability we hop forward 4 weeks at a time until we find some — capped
  // at ~6 months so we never spin forever. Then the next effect scrolls to it.
  const initialAdvanceRef = useRef(0);
  useEffect(() => {
    if (step !== 2 || days.length === 0) return;
    const hasAvail = days.some(d =>
      d.allowed !== false && (d.slots || []).some(s => s.status === "available")
    );
    if (hasAvail) {
      initialAdvanceRef.current = 0;
      return;
    }
    if (initialAdvanceRef.current >= 6) return; // ~24 weeks ahead, give up
    initialAdvanceRef.current += 1;
    setWeekStart(prev => new Date(prev.getTime() + 28 * ADAY));
  }, [step, days]);

  // After the day list renders / updates, smooth-scroll the FIRST day with
  // an available slot into view so mobile users don't have to hunt.
  useEffect(() => {
    if (step !== 2 || days.length === 0) return;
    const firstAvail = days.find(day =>
      day.allowed !== false && (day.slots || []).some(s => s.status === "available")
    );
    if (!firstAvail) return;
    // Wait a frame for the DOM to commit.
    const id = window.setTimeout(() => {
      const el = document.querySelector(`[data-testid="day-row-${firstAvail.date}"]`);
      if (!el) return;
      // Offset the scroll so the sticky header (site nav 84px + step-2 header
      // ~110px on mobile) doesn't cover the target row.
      const headerH = window.innerWidth < 768 ? 200 : 0;
      const rect = el.getBoundingClientRect();
      const targetY = window.scrollY + rect.top - headerH;
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }, 120);
    return () => window.clearTimeout(id);
  }, [step, days]);

  // When the user has picked the full set of lessons their package needs,
  // smooth-scroll the Continue button into view (mobile only — desktop has
  // the sticky order-summary on the right).
  useEffect(() => {
    if (step !== 2) return;
    if (selectedSlots.length !== required) return;
    if (typeof window === "undefined" || window.innerWidth >= 768) return;
    const id = window.setTimeout(() => {
      continueBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(id);
  }, [step, selectedSlots.length, required]);

  const slotKey = (s) => `${s.date}|${s.time}`;
  const isSelected = (date, time) => selectedSlots.some(s => s.date === date && s.time === time);

  const toggleSlot = (date, time, status) => {
    if (status !== "available") return;
    const key = slotKey({ date, time });
    if (isSelected(date, time)) {
      setSelectedSlots(prev => prev.filter(s => slotKey(s) !== key));
    } else {
      if (selectedSlots.length >= required) {
        toast.warning(`This package needs exactly ${required} slot${required > 1 ? 's' : ''}. Unselect one first.`);
        return;
      }
      setSelectedSlots(prev => [...prev, { date, time }]);
    }
  };

  const onProceedToDetails = () => {
    if (selectedSlots.length !== required) {
      toast.error(`Please select exactly ${required} slot${required > 1 ? 's' : ''}.`);
      return;
    }
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onCheckout = async (e) => {
    e.preventDefault();
    const errs = validateDetails();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const first = ["name", "email", "phone", "pickup_address", "pickup_town", "pickup_postcode"].find(k => errs[k]);
      const map = { pickup_address: "address", pickup_town: "town", pickup_postcode: "postcode" };
      const tid = `booking-${map[first] || first}`;
      const el = document.querySelector(`[data-testid="${tid}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setFieldErrors({});
    try {
      setSubmitting(true);
      const { name, email, phone, pickup_address, pickup_town, pickup_postcode } = details;
      const sorted = [...selectedSlots].sort((a,b) => (a.date + a.time).localeCompare(b.date + b.time));
      const payload = {
        product, transmission, slots: sorted,
        customer_name: name, customer_email: email, customer_phone: phone,
        pickup_address, pickup_town, pickup_postcode, notes: details.notes,
      };
      const res = await createCheckout(payload);
      if (res.checkout_url) window.location.href = res.checkout_url;
      else toast.error("Could not start checkout.");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      if (Array.isArray(raw) && raw.length > 0) {
        const fieldMap = {
          customer_name: "name", customer_email: "email", customer_phone: "phone",
          pickup_address: "pickup_address", pickup_town: "pickup_town", pickup_postcode: "pickup_postcode",
        };
        const newErrs = {};
        raw.forEach(d => {
          const apiField = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
          const uiField = fieldMap[apiField];
          if (uiField) newErrs[uiField] = d.msg || "Invalid";
        });
        if (Object.keys(newErrs).length > 0) {
          setFieldErrors(newErrs);
        } else {
          toast.error("Could not start checkout. Please double-check your details and try again.");
        }
      } else if (typeof raw === "string") {
        toast.error(raw);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally { setSubmitting(false); }
  };

  // Weeks (4 weeks shown)
  const weekDays = useMemo(() => {
    const four = [];
    let cursor = new Date(weekStart);
    for (let i = 0; i < 28; i++) {
      const d = new Date(cursor); d.setDate(cursor.getDate() + i);
      four.push(isoDate(d));
    }
    return four;
  }, [weekStart]);

  return (
    <>
      <Seo title="Book driving lessons — Lime Driving School" description="Book your driving lessons in Southampton in three simple steps." />
      <PageHero
        eyebrow="Book your lessons"
        title="Choose your package"
        subtitle="Pick a package, then a slot that works for you, then pay securely with Stripe. Lessons take roughly 2 hours each."
        showCta={false}
      />
      <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="book-page">
        <Link to="/" className="text-sm text-[#4B5563] hover:text-[#111827] inline-flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back home</Link>

        {/* Stepper */}
        <div className="mt-6 flex items-center gap-3 text-sm" data-testid="booking-stepper">
          {[
            { n: 1, label: "Choose package" },
            { n: 2, label: "Pick slots" },
            { n: 3, label: "Your details" },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-medium ${step >= s.n ? 'bg-[#37B160] text-white' : 'bg-black/5 text-[#9CA3AF]'}`}>{s.n}</span>
              <span className={step >= s.n ? 'text-[#111827]' : 'text-[#9CA3AF]'}>{s.label}</span>
              {i < 2 && <span className="text-[#E5E7EB] mx-1">/</span>}
            </div>
          ))}
        </div>

        {/* Step 1: Product + transmission */}
        {step === 1 && (
          <div className="mt-10 grid md:grid-cols-12 gap-8" data-testid="step-product">
            <div className="md:col-span-7">
              <h2 className="text-xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Transmission</h2>
              <RadioGroup value={transmission} onValueChange={setTransmission} className="mt-4 grid grid-cols-2 gap-3">
                <div className={`p-5 rounded-2xl border cursor-pointer ${transmission === 'manual' ? 'border-[#37B160] bg-[#E8F5EC] ring-2 ring-[#37B160]/30' : 'border-black/10'}`}>
                  <RadioGroupItem value="manual" id="tx-manual" className="hidden" />
                  <Label htmlFor="tx-manual" className="cursor-pointer" data-testid="transmission-manual">
                    <div className="font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Manual</div>
                    <div className="text-xs text-[#4B5563] mt-1">Mon–Thu &amp; Sat</div>
                  </Label>
                </div>
                <div className={`p-5 rounded-2xl border cursor-pointer ${transmission === 'auto' ? 'border-[#37B160] bg-[#E8F5EC] ring-2 ring-[#37B160]/30' : 'border-black/10'}`}>
                  <RadioGroupItem value="auto" id="tx-auto" className="hidden" />
                  <Label htmlFor="tx-auto" className="cursor-pointer" data-testid="transmission-auto">
                    <div className="font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Automatic</div>
                    <div className="text-xs text-[#4B5563] mt-1">Friday only</div>
                  </Label>
                </div>
              </RadioGroup>

              <h2 className="mt-10 text-xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Package</h2>
              <div className="mt-4 space-y-3">
                {Object.entries(PRODUCTS).filter(([k]) => k !== "bespoke").map(([k, v]) => (
                  <label key={k}
                         className={`flex items-start gap-4 p-5 rounded-2xl border cursor-pointer ${product === k ? 'border-[#37B160] bg-[#E8F5EC] ring-2 ring-[#37B160]/30' : 'border-black/10 hover:border-black/20'}`}
                         data-testid={`product-radio-${k}`}>
                    <input type="radio" name="product" value={k} checked={product === k} onChange={() => handlePickPackage(k)} className="mt-1" />
                    <div>
                      <div className="font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{v.label}</div>
                      <div className="text-sm text-[#4B5563]">{v.slots} × 2-hour slot{v.slots > 1 ? 's' : ''} · {v.hours} hours total</div>
                    </div>
                  </label>
                ))}
                <Link to="/packages/bespoke" className="flex items-start gap-4 p-5 rounded-2xl border border-dashed border-black/15 hover:border-black/30" data-testid="bespoke-call-cta">
                  <span className="mt-1 h-4 w-4 rounded-full border border-black/30 grid place-items-center"><span className="h-1.5 w-1.5 rounded-full bg-transparent" /></span>
                  <div>
                    <div className="font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Intensive Courses</div>
                    <div className="text-sm text-[#4B5563]">Pass fast — 10, 20 or 35 hour options. See intensive course details.</div>
                  </div>
                </Link>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="bg-white border border-black/5 rounded-2xl p-6 sticky top-20" data-testid="booking-summary">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Order summary</div>
                <h3 className="mt-2 text-lg font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{quote?.product_label || PRODUCTS[product]?.label}</h3>
                <div className="mt-1 text-sm text-[#4B5563]">{transmission === "manual" ? "Manual" : "Automatic"} · {required} slot{required > 1 ? 's' : ''}</div>
                <div className="mt-5 pt-5 border-t border-black/5 flex items-baseline justify-between">
                  <span className="text-sm text-[#4B5563]">Total</span>
                  <span className="text-3xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{quote ? formatGBP(quote.amount) : '£—'}</span>
                </div>
                <Button ref={pickLessonsBtnRef} onClick={() => { setSelectedSlots([]); setStep(2); }} className="mt-6 w-full bg-[#37B160] hover:bg-[#2D9450] text-white h-12" data-testid="step-1-next">
                  Pick Lessons <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Slots */}
        {step === 2 && (
          <div className="mt-10 grid md:grid-cols-12 gap-8" data-testid="step-slots">
            <div className="md:col-span-8">
              {/* Sticky on mobile so the title + month + nav stay visible while
                  scrolling through the day list. The site header is 84px tall,
                  so anchor the sticky band just below it (pt-4 gives breathing
                  room so the title is never clipped by the site nav). */}
              <div className="sticky top-[84px] z-20 -mx-4 px-4 pt-4 pb-3 bg-[#FAFAFA]/95 backdrop-blur supports-[backdrop-filter]:bg-[#FAFAFA]/80 border-b border-black/5 md:static md:bg-transparent md:backdrop-blur-none md:border-0 md:mx-0 md:px-0 md:py-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>
                  Select {required} lesson{required > 1 ? "s" : ""}
                </h1>
                <div className="flex items-center gap-2 mt-2 md:mt-4">
                  <Button size="sm" variant="outline" className="border-black/15" data-testid="prev-week"
                          onClick={() => setWeekStart(new Date(weekStart.getTime() - 28 * ADAY))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-black/15" data-testid="next-week"
                          onClick={() => setWeekStart(new Date(weekStart.getTime() + 28 * ADAY))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <div className="ml-2 text-sm font-medium text-[#111827]" data-testid="month-label" style={{fontFamily:"Outfit, sans-serif"}}>
                    {monthLabel}
                  </div>
                </div>
              </div>

              <div ref={slotListRef} className="mt-6 space-y-2" data-testid="slot-list-container">
                {days.length === 0 && <div className="text-[#9CA3AF] text-sm p-6 bg-white border border-black/5 rounded-2xl">Loading availability…</div>}
                {days.map(day => {
                  const isAllowed = day.allowed;
                  const dayDate = new Date(day.date + "T00:00:00");
                  const isPast = day.slots.every(s => s.status === "blocked") && !isAllowed;
                  return (
                    <div key={day.date} className={`flex items-center gap-3 p-3 rounded-xl border ${isAllowed ? 'bg-white border-black/5' : 'bg-[#F3F4F6] border-transparent'}`} data-testid={`day-row-${day.date}`}>
                      <div className="w-28 flex-none">
                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">{dayDate.toLocaleDateString("en-GB", { weekday: "short" })}</div>
                        <div className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{dayDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div>
                      </div>
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        {day.slots.map(s => {
                          const isSel = isSelected(day.date, s.time);
                          const disabled = s.status !== "available";
                          const label = `${s.time}–${endTime(s.time)}`;
                          let cls = "h-12 text-sm font-medium rounded-lg border transition-colors text-center";
                          if (isSel) cls += " bg-[#37B160] border-[#37B160] text-white";
                          else if (disabled) cls += " bg-transparent border-black/5 text-[#9CA3AF] cursor-not-allowed";
                          else cls += " bg-white border-black/10 hover:border-[#37B160] hover:bg-[#E8F5EC] text-[#111827] cursor-pointer";
                          return (
                            <button key={s.time} onClick={() => toggleSlot(day.date, s.time, s.status)}
                                    disabled={disabled} className={cls}
                                    data-testid={`slot-${day.date}-${s.time.replace(':','')}`}>
                              {label}
                              {s.status === "booked" && <span className="block text-[10px] text-[#9CA3AF] mt-0.5">Booked</span>}
                              {s.status === "locked" && <span className="block text-[10px] text-[#9CA3AF] mt-0.5">Held</span>}
                              {s.status === "blocked" && isAllowed === false && !isPast && (
                                <span className="block text-[10px] text-[#9CA3AF] mt-0.5">N/A</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {days.length > 0 && (
                  <div className="flex items-center gap-2 pt-4" data-testid="slot-nav-bottom">
                    <Button size="sm" variant="outline" className="border-black/15" data-testid="prev-week-bottom"
                            onClick={() => setWeekStart(new Date(weekStart.getTime() - 28 * ADAY))}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="border-black/15" data-testid="next-week-bottom"
                            onClick={() => { setWeekStart(new Date(weekStart.getTime() + 28 * ADAY)); window.scrollTo({ top: slotListRef.current?.offsetTop ? slotListRef.current.offsetTop - 180 : 0, behavior: "smooth" }); }}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <div className="ml-2 text-sm font-medium text-[#111827]" style={{fontFamily:"Outfit, sans-serif"}}>
                      {monthLabel}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-4">
              <div className="bg-white border border-black/5 rounded-2xl p-6 sticky top-20" data-testid="summary-step2">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Your selection</div>
                <h3 className="mt-2 text-lg font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{quote?.product_label}</h3>
                <div className="text-sm text-[#4B5563]">{transmission === "manual" ? "Manual" : "Automatic"} · {selectedSlots.length}/{required} selected</div>
                <ul className="mt-4 space-y-1 max-h-60 overflow-auto">
                  {selectedSlots.map(s => (
                    <li key={slotKey(s)} className="flex items-center justify-between text-sm">
                      <span>{formatDate(s.date)} · {s.time}</span>
                      <button onClick={() => toggleSlot(s.date, s.time, "available")} className="text-[#9CA3AF] hover:text-[#111827]" data-testid={`remove-slot-${s.date}-${s.time.replace(':','')}`}>×</button>
                    </li>
                  ))}
                  {selectedSlots.length === 0 && <li className="text-sm text-[#9CA3AF]">No slots yet — tap a time on the left.</li>}
                </ul>
                <div className="mt-5 pt-5 border-t border-black/5 flex items-baseline justify-between">
                  <span className="text-sm text-[#4B5563]">Total</span>
                  <span className="text-2xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{quote ? formatGBP(quote.amount) : '£—'}</span>
                </div>
                <Button onClick={() => setStep(1)} variant="outline" className="mt-5 w-full border-black/15" data-testid="step-2-back">Back</Button>
                <Button ref={continueBtnRef} onClick={onProceedToDetails} disabled={selectedSlots.length !== required}
                        className="mt-2 w-full bg-[#37B160] hover:bg-[#2D9450] text-white h-12 disabled:opacity-40 disabled:bg-[#37B160]"
                        data-testid="step-2-next">
                  Continue <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && (
          <form onSubmit={onCheckout} className="mt-10 grid md:grid-cols-12 gap-8" data-testid="step-details">
            <div className="md:col-span-7">
              <h1 className="text-2xl sm:text-3xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Your details</h1>
              <p className="mt-2 text-sm text-[#4B5563]">We use these to confirm your booking and arrange pickup.</p>
              <div className="mt-6 space-y-4">
                <FormField label="Full name" error={fieldErrors.name}>
                  <Input className={`h-11 ${fieldErrors.name ? "border-red-400 focus-visible:ring-red-200" : ""}`}
                         placeholder="Full name" value={details.name}
                         onChange={e => updateDetail("name", e.target.value)}
                         data-testid="booking-name" />
                </FormField>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label="Email" error={fieldErrors.email}>
                    <Input className={`h-11 ${fieldErrors.email ? "border-red-400 focus-visible:ring-red-200" : ""}`}
                           type="email" placeholder="Email" value={details.email}
                           onChange={e => updateDetail("email", e.target.value)}
                           data-testid="booking-email" />
                  </FormField>
                  <FormField label="Phone" error={fieldErrors.phone}>
                    <Input className={`h-11 ${fieldErrors.phone ? "border-red-400 focus-visible:ring-red-200" : ""}`}
                           placeholder="Phone" value={details.phone}
                           onChange={e => updateDetail("phone", e.target.value)}
                           data-testid="booking-phone" />
                  </FormField>
                </div>
                <FormField label="Pickup address" error={fieldErrors.pickup_address}>
                  <Textarea className={fieldErrors.pickup_address ? "border-red-400 focus-visible:ring-red-200" : ""}
                            placeholder="Pickup address (street / house number)" value={details.pickup_address}
                            onChange={e => updateDetail("pickup_address", e.target.value)}
                            rows={2} data-testid="booking-address" />
                </FormField>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label="Town" error={fieldErrors.pickup_town}>
                    <Input className={`h-11 ${fieldErrors.pickup_town ? "border-red-400 focus-visible:ring-red-200" : ""}`}
                           placeholder="Town" value={details.pickup_town}
                           onChange={e => updateDetail("pickup_town", e.target.value)}
                           data-testid="booking-town" />
                  </FormField>
                  <FormField label="Postcode" error={fieldErrors.pickup_postcode}>
                    <Input className={`h-11 ${fieldErrors.pickup_postcode ? "border-red-400 focus-visible:ring-red-200" : ""}`}
                           placeholder="Postcode" value={details.pickup_postcode}
                           onChange={e => updateDetail("pickup_postcode", e.target.value.toUpperCase())}
                           data-testid="booking-postcode" />
                  </FormField>
                </div>
                <Textarea placeholder="Anything we should know? (optional)" value={details.notes} onChange={e => setDetails({...details, notes: e.target.value})} rows={2} data-testid="booking-notes" />
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs text-[#9CA3AF]">
                <AlertTriangle className="h-3.5 w-3.5" /> Payment is processed by Stripe. Your card details never touch our servers.
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="bg-white border border-black/5 rounded-2xl p-6 sticky top-20" data-testid="summary-step3">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">Final summary</div>
                <h3 className="mt-2 text-lg font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{quote?.product_label}</h3>
                <div className="text-sm text-[#4B5563]">{transmission === "manual" ? "Manual" : "Automatic"}</div>
                <ul className="mt-4 space-y-1 max-h-60 overflow-auto">
                  {[...selectedSlots].sort((a,b) => (a.date + a.time).localeCompare(b.date + b.time)).map(s => (
                    <li key={slotKey(s)} className="text-sm flex items-start gap-2">
                      <CalendarDays className="h-4 w-4 text-[#37B160] mt-0.5" />
                      <span>{formatLongDate(s.date)} · {s.time}–{endTime(s.time)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-5 border-t border-black/5 flex items-baseline justify-between">
                  <span className="text-sm text-[#4B5563]">Total</span>
                  <span className="text-2xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{quote ? formatGBP(quote.amount) : '£—'}</span>
                </div>
                <Button type="button" onClick={() => setStep(2)} variant="outline" className="mt-5 w-full border-black/15" data-testid="step-3-back">Back</Button>
                <Button type="submit" disabled={submitting} className="mt-2 w-full bg-[#37B160] hover:bg-[#2D9450] text-white h-12" data-testid="checkout-btn">
                  {submitting ? "Redirecting to Stripe..." : "Pay with Stripe"} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </form>
        )}
      </section>
    </>
  );
}

function FormField({ label, error, children }) {
  return (
    <div>
      {children}
      {error && (
        <p className="mt-1.5 text-xs text-red-600 flex items-start gap-1" role="alert" data-testid="field-error">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-none" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
